import React from "react";
import { Link } from "react-router-dom";
import { FaInstagram } from "react-icons/fa"
export default function TimeManagementPlan() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Time Management for Assignments: A 7-Day Plan (Pomodoro Included)
          </h1>
          <p className="mt-3 text-gray-300">
            Time Management for Assignments in a 7-day plan: Pomodoro, time blocking, batching, templates, and checklists to finish early with less stress. Ready.
          </p>
      
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#keypoints" className="block hover:text-white">5 Key Points</a>
                <a href="#how" className="block hover:text-white">How the 7-Day Plan Works</a>
                <a href="#comparison" className="block hover:text-white">Pomodoro vs Time Blocking</a>
                <a href="#weekly" className="block hover:text-white">Weekly Schedule Template</a>
                <a href="#7day" className="block hover:text-white">The 7-Day Plan</a>
                <a href="#batching" className="block hover:text-white">Batching & Eisenhower</a>
                <a href="#workflow" className="block hover:text-white">Research & Referencing Workflow</a>
                <a href="#miniplaybooks" className="block hover:text-white">Online & Working Students</a>
                <a href="#energy" className="block hover:text-white">Energy Management</a>
                <a href="#troubleshooting" className="block hover:text-white">Troubleshooting</a>
                <a href="#help" className="block hover:text-white">How we help</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Introduction */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                Time Management for Assignments is simply turning a messy week into a clear 7-day roadmap. This plan blends the Pomodoro Technique, time blocking/calendar blocking, and task batching to prioritize with the Eisenhower Matrix, protect deep work, and finish early without all-nighters.
              </p>
              <p className="text-gray-200 mt-3">
                You’ll break big tasks into small actions, schedule them in Google Calendar/Notion, and review daily using active recall and spaced repetition so your progress feels steady, human, and repeatable.
              </p>
            </section>

            {/* Key Points */}
            <section id="keypoints" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-4">5 key points</h2>
              <ul className="list-disc pl-6 space-y-3 text-gray-200">
                <li><strong>Plan the week:</strong> List deadlines, set SMART goals, and time-block study windows in your calendar.</li>
                <li><strong>Work in intervals:</strong> Break tasks into Pomodoro-sized actions (25/5) and add a 20% buffer.</li>
                <li><strong>Batch similar work:</strong> Group research, APA/Harvard citations, and drafting to cut context switching.</li>
                <li><strong>Review nightly:</strong> Log Pomodoros, move tasks in Trello/Notion, and reschedule the next day’s top 3.</li>
                <li><strong>Protect focus & energy:</strong> Use Focus Mode, single-task deep work, sleep well, and take short movement breaks.</li>
              </ul>
            </section>

            {/* How it works */}
            <section id="how" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Time Management for Assignments: How the 7-Day Plan Works</h2>
              <p className="text-gray-200">
                Prioritize with the Eisenhower Matrix. Sort tasks into Urgent & Important, Important (Not Urgent), Urgent (Not Important), and Neither. This keeps the high-impact work first.
              </p>
              <p className="text-gray-200 mt-3">
                Atomize everything. Big tasks are procrastination magnets. Convert them into actionable subtasks that fit one or two Pomodoros (e.g., “Find 5 peer-reviewed sources,” “Draft methods paragraph,” “Format APA/Harvard references”).
              </p>
              <p className="text-gray-200 mt-3">
                Time-block your calendar. Put focus blocks on your calendar like appointments. If it’s not scheduled, it’s optional.
              </p>
              <p className="text-gray-200 mt-3">
                Protect deep work. Switch on Focus Mode, silence notifications, use site blockers, and go single-task. Every switch adds cognitive load.
              </p>
              <p className="text-gray-200 mt-3">
                Review daily. End each day with a 5-minute retro: What worked? What slid? Move the remaining atoms forward on your Trello board (To-Do → Doing → Done).
              </p>
             <p className="text-gray-200 mt-3">
  Use SMART goals. Define clear outcomes and timeboxes (e.g., “Complete 3 Pomodoros to outline Results section by 6 PM”)  
  <Link 
    to="/blog/WriteWithAI" 
    className="text-rose-400 font-semibold hover:text-rose-300 underline ml-1"
  >
    avoid mistakes in assignment writing
  </Link>.
</p>

            </section>

            {/* Comparison */}
            <section id="comparison" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Pomodoro vs. Time Blocking for Assignments (Intervals vs. Blocks)</h2>
              <p className="text-gray-200">
                Different tasks need different focus formats. Use this quick comparison to pick your interval based on chronotype, task difficulty, and deadline proximity.
              </p>

              {/* Inserted table matching the Google Doc screenshot */}
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full table-auto border-collapse border border-white/20 text-gray-200">
                  <thead>
                    <tr className="bg-white/3">
                      <th className="border border-white/10 px-4 py-3 text-left">Format</th>
                      <th className="border border-white/10 px-4 py-3 text-left">Work</th>
                      <th className="border border-white/10 px-4 py-3 text-left">Break</th>
                      <th className="border border-white/10 px-4 py-3 text-left">Best For</th>
                      <th className="border border-white/10 px-4 py-3 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-white/10 px-4 py-3 font-semibold text-gray-100">25/5 Pomodoro</td>
                      <td className="border border-white/10 px-4 py-3">25 min</td>
                      <td className="border border-white/10 px-4 py-3">5 min</td>
                      <td className="border border-white/10 px-4 py-3">Problem sets, admin, early drafting</td>
                      <td className="border border-white/10 px-4 py-3">Low friction; frequent checkpoints</td>
                    </tr>
                    <tr className="bg-white/5">
                      <td className="border border-white/10 px-4 py-3 font-semibold text-gray-100">50/10 Block</td>
                      <td className="border border-white/10 px-4 py-3">50 min</td>
                      <td className="border border-white/10 px-4 py-3">10 min</td>
                      <td className="border border-white/10 px-4 py-3">Dense reading, referencing, outlining</td>
                      <td className="border border-white/10 px-4 py-3">Fewer interruptions; deeper timeboxing</td>
                    </tr>
                    <tr>
                      <td className="border border-white/10 px-4 py-3 font-semibold text-gray-100">90/20 Deep Block</td>
                      <td className="border border-white/10 px-4 py-3">90 min</td>
                      <td className="border border-white/10 px-4 py-3">20 min</td>
                      <td className="border border-white/10 px-4 py-3">Heavy writing, complex logic</td>
                      <td className="border border-white/10 px-4 py-3">Aligns with ultradian rhythms; requires energy</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 text-gray-200">
                <p className="font-semibold">Decision cues:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>New or fidgety? Start 25/5.</li>
                  <li>Reading dense papers? Try 50/10.</li>
                  <li>Flowing on a draft? Lock in 90/20.</li>
                  <li>Close to a deadline? Batch and extend blocks, then add a 20% buffer.</li>
                </ul>
              </div>
            </section>

            {/* Weekly Template */}
            <section id="weekly" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Weekly Schedule Template for Assignments</h2>
              <h3 className="text-lg font-semibold text-white mt-2">How to use it</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200 mt-3">
                <li>Run a 3-day time audit. Log your time in 15-minute chunks. Circle energy peaks (your biological prime time).</li>
                <li>Place non-negotiables. Classes, job shifts, family time.</li>
                <li>Block study windows. 2–3 per day, aiming for 4–8 Pomodoros total in heavy weeks.</li>
                <li>Batch by type. Research together, citations together, drafting together to avoid context switching.</li>
                <li>Add a buffer. Reserve a daily 30–60 minute block for spillover and surprises (planning fallacy insurance).</li>
                <li>Review nightly. Use the “Day-End Checklist” tab in the download.</li>
                <li>Rule-of-thumb study time: During heavy weeks, expect multi-hour study windows each day. Use the planner to spread that load evenly instead of cramming.</li>
              </ul>
            </section>

            {/* 7-Day Plan */}
            <section id="7day" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">The 7-Day Plan (Pomodoro Built-In)</h2>
              <p className="text-gray-200">Each “focus block” below = 2–4 Pomodoros with short breaks and one long break.</p>

              <div className="mt-4 space-y-4 text-gray-200">
                <div>
                  <h3 className="font-semibold text-white">Day 1 — Map, Measure, Make It Real</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Open every brief/rubric; note word count, deadline, required style (APA/Harvard).</li>
                    <li>Sketch a mini Gantt chart per assignment (Research → Outline → Draft → Revise → Proof → References → Submit). Highlight the Critical Path tasks that, if delayed, delay everything.</li>
                    <li>Atomize tasks into Pomodoro-sized actions.</li>
                    <li>Prioritize via Eisenhower Matrix; define SMART goals for today.</li>
                    <li>Time blocking: add 3–4 Pomodoros to your Google Calendar/Notion.</li>
                    <li>Tools: install Forest app or Focus To-Do; set up Trello/Todoist.</li>
                    <li>Pomodoro dose: 3–4 to build momentum.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 2 — Research Sprints & Source Triage</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Task batching: group research by question/section.</li>
                    <li>After each source, use active recall: close the tab, write a 3-sentence summary from memory, then verify.</li>
                    <li>Capture quotes with page numbers and draft citation entries (saves hours later).</li>
                    <li>Decide which sources truly serve the argument (Pareto Principle: the vital 20%).</li>
                    <li>Pomodoro dose: 6–8 across the day.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 3 — Outlines That Write Themselves</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Turn rubric points into headings/subheadings.</li>
                    <li>Insert 1–2 strong citations per subsection.</li>
                    <li>Draft a thesis and signpost paragraph.</li>
                    <li>For multiple assignments, outline each lightly to keep momentum.</li>
                    <li>Pomodoro dose: ~6.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 4 — First Draft (Deep Work)</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Go full-screen; notifications off.</li>
                    <li>Write in sprints; don’t edit while drafting (drop “TK” where detail is missing).</li>
                    <li>Move from easier sections to tougher ones to maintain flow.</li>
                    <li>Pomodoro dose: 8–10 (consider 50/10 or 90/20 if you’re in flow).</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 5 — Revise Structure & Evidence</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Global pass: Do sections follow a logical arc? Clear topic sentences? Transitions?</li>
                    <li>Evidence pass: Upgrade weak citations; ensure every claim is backed.</li>
                    <li>Formatting pass: Apply APA/Harvard rules consistently (headings, in-text, references).</li>
                    <li>Rotate across assignments (A → B → A) to keep fresh eyes.</li>
                    <li>Pomodoro dose: 6–8.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 6 — Polish, Proof, Prepare</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Tighten sentences; favor active voice; remove filler.</li>
                    <li>Consistency checks (terms, tense, table/figure labels).</li>
                    <li>Alphabetize references; check DOI/URL formatting.</li>
                    <li>If slides are needed, build them now from the outline.</li>
                    <li>Pomodoro dose: 5–7.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white">Day 7 — Buffer, Backup, Brain Reset</h3>
                  <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-200">
                    <li>Use a buffer block to clear leftovers.</li>
                    <li>Submission checklist: file name, format (PDF/required), references complete, figures visible.</li>
                    <li>Backup to cloud + email a copy to yourself.</li>
                    <li>Spaced repetition: 2–3 short Pomodoros of quiz/flashcard review.</li>
                    <li>Pomodoro dose: 3–4 (keep it light).</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Batching */}
            <section id="batching" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Batching, Deep Work, and the Eisenhower Matrix (In Practice)</h2>
              <p className="text-gray-200">
                Before (context switching tax):
              </p>
              <pre className="text-gray-200 bg-white/3 p-3 rounded"> Research → message reply → draft → format citation → DM → search figure → draft…</pre>
              <p className="text-gray-200 mt-3">After (task batching):</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Block 1: 3 Pomodoros research only (links to “Research Inbox”).</li>
                <li>Block 2: 2 Pomodoros citations only (Zotero/Mendeley; APA/Harvard templates).</li>
                <li>Block 3: 3 Pomodoros draft only (no tabs, no inbox).</li>
              </ul>
              <p className="text-gray-200 mt-3">You’ll feel less scattered, and your output quality jumps because attention isn’t fragmented.</p>
            </section>

            {/* Workflow */}
            <section id="workflow" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Research & Referencing Workflow (APA/Harvard-Ready)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Capture sources in Zotero/Mendeley with tags per assignment/section.</li>
                <li>Cite-as-you-go in Google Docs/Word using the plugin; choose APA or Harvard early.</li>
                <li>For quotes, paste page numbers into your notes immediately.</li>
                <li>On Day 5, run a format sweep (headings, in-text, reference list).</li>
                <li>Final pass with Grammarly/Hemingway for clarity—then do one human read-aloud.</li>
              </ul>
            </section>

            {/* Mini-playbooks */}
            <section id="miniplaybooks" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Online & Working Students: Two Mini-Playbooks</h2>
              <div className="mt-3 text-gray-200">
                <h3 className="font-semibold text-white">If you study online</h3>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Time-block asynchronous lectures like live classes.</li>
                  <li>Micro-deadlines: draft forum posts the day the topic drops; reply the next morning.</li>
                  <li>Use 50/10 for lecture note-taking; one 25/5 to summarize key takeaways.</li>
                </ul>
              </div>

              <div className="mt-4 text-gray-200">
                <h3 className="font-semibold text-white">If you work part-time</h3>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Turn commute time into audio notes or retrieval practice.</li>
                  <li>Negotiate one predictable study block with your manager (even 60–90 min helps).</li>
                  <li>Batch admin (emails, forms) into a single 25/5 to protect creative energy.</li>
                </ul>
              </div>
            </section>

            {/* Energy management */}
            <section id="energy" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Energy Management: Work With (Not Against) Your Chronotype</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Schedule heavy deep work during your biological prime time.</li>
                <li>Protect sleep; cognitive performance collapses without it.</li>
                <li>Move your body: a 15-minute walk between blocks resets attention.</li>
              </ul>
            </section>

            {/* Troubleshooting */}
            <section id="troubleshooting" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Troubleshooting & Quick Fixes</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Can’t start? Do the smallest atom (open doc, write one heading). Momentum beats motivation.</li>
                <li>Over-planned? Embrace Parkinson’s Law: shorten sessions, add a daily buffer.</li>
                <li>Too much research, no writing? Cap research to 3 Pomodoros, then must draft.</li>
                <li>Draft off-length? Use the Pareto Principle: double down on the 20% sections worth most marks; trim tangents.</li>
              </ul>
            </section>

            {/* How we help */}
            <section id="help" style={{ scrollMarginTop: "7rem" }} className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-2">How we help</h2>
              <p className="text-gray-200">
                If you’d like guided support while implementing Time Management for Assignments, Infinite Solution can help you turn this 7-day plan into a personalized study system.
              </p>
              <p className="text-gray-200 mt-3">
                Our mentors co-create your weekly time blocking calendar in Google Calendar/Notion, set SMART goals with the Eisenhower Matrix, and coach you through Pomodoro Technique sprints for research, outlining, and drafting.
              </p>
              <p className="text-gray-200 mt-3">
                We also streamline APA/Harvard referencing (with citation managers like Zotero/Mendeley), provide structure feedback, and share checklists for active recall and spaced repetition, all with research-based, plagiarism-checked, original guidance and responsive support.
              </p>
              <p className="text-gray-200 mt-3">
                Think of it as a human layer on top of your tools (Trello/Todoist, focus timers) so you stay focused, finish early, and submit with confidence.
              </p>
            </section>

            {/* FAQs */}
            <section id="faqs" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs</h2>
              <ol className="list-decimal pl-6 space-y-3 text-gray-200">
                <li>
                  <p className="font-semibold">Q1. What’s the best Pomodoro length for dense reading?</p>
                  <p>Try 50/10 (fewer interruptions) or 90/20 during peak energy.</p>
                </li>
                <li>
                  <p className="font-semibold">Q2. How many hours should I study per day in heavy weeks?</p>
                  <p>Plan multi-hour windows daily (e.g., 4–8 Pomodoros) and spread them across the week.</p>
                </li>
                <li>
                  <p className="font-semibold">Q3. Does batching really beat multitasking?</p>
                  <p>Yes. Grouping like tasks reduces context switching and decision fatigue.</p>
                </li>
                <li>
                  <p className="font-semibold">Q4. Is Pomodoro useful if I have ADHD?</p>
                  <p>Often, yes. Short intervals and clear breaks help. Customize durations and environment.</p>
                </li>
              </ol>
            </section>

            {/* Conclusion */}
            <section id="conclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion: Make the Plan Yours (and Repeat Weekly)</h2>
              <p className="text-gray-200">
                Time management for assignments is really about clarity and consistency. With a weekly loop, prioritize, atomize, time-block, focus with the Pomodoro Technique, then review, you’ll finish earlier, write better, and feel calmer.
              </p>
              <p className="text-gray-200 mt-3">
                Start now: drop three Pomodoros into your calendar for your highest-value task and protect one deep-work block today.
              </p>
            </section>

            <section
              id="acknowledgement"
              style={{ scrollMarginTop: "7rem" }}
              className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
            >
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
                Acknowledgement
              </h2>
            
              <p className="text-gray-200">
                The concept was first introduced by{" "}
                <span className="font-semibold text-white">Joy Barua</span>, who set the foundation for the content’s direction.
                <br />
                <a
                  href="https://www.instagram.com/j.b_12111990?igsh=ejBoa2dqbmI1OG4z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
                >
                  <FaInstagram /> @joybarua
                </a>
              </p>
            
              <p className="text-gray-200 mt-4">
                To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
                <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
                developed, our team at{" "}
                <a
                  href="https://www.infinitesolutionedu.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold hover:text-blue-300 underline"
                >
                  Infinite Solution
                </a>{" "}
                took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
                audience.
              </p>
            
              <p className="text-gray-200 mt-4">
                This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
                is impactful, high-performing, and highly relevant to readers.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
